import * as React from 'react';
import { useRef, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import NewHFLLayout from './NewHFLLayout';
import { newHFLTypography } from '../../hd-ui-kit/NewHFLTheme';
import formData from '../../utils/form-data';
import { COUNTRIES } from '../../utils/form-data/countries';
import { validateFields, validationOptions } from '../../ValidationSchema';
import eventBus from '../../Eventbus';

const phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance();

const MAX_RESUME_BYTES = 2097152;
const ACCEPTED_RESUME_TYPES = '.pdf,.docx,.doc,.rtf,.txt';
const MAX_RESUME_NAME_LENGTH = 100;
const ACCEPTED_RESUME_MIME_TYPES = [
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/rtf',
];
const RESUME_NAME_PATTERN = new RegExp('^[a-zA-Z0-9_\\-_\\s.]*$'); //NOSONAR
const DEFAULT_COUNTRY_VALUE = 'United States of America';
const DEFAULT_MOBILE_COUNTRY_CODE = '+1';

function UploadFileIcon(props: SvgIconProps) {
  return (
    <SvgIcon viewBox="0 0 24 24" {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6 2h8l6 6v12c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2zm7 1.5V9h5.5zM13 19v-4.16l1.59 1.59L16 15.01 12.01 11 8 15.01l1.41 1.41L11 14.84V19z"
      />
    </SvgIcon>
  );
}

const SCBackImage = styled(Box)(({ theme }) => ({
  display: 'none',
  position: 'absolute',
  width: '100%',
  height: '100%',
  content: '""',
  backgroundImage: `url(${window.location.origin}/res/hd-ui-kit-images/newHFLDotMatrix.svg)`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 2.7% top 66px',
  right: 0,
  top: 0,
  zIndex: 0,
  pointerEvents: 'none',
  [theme.breakpoints.up('lg')]: {
    display: 'block',
    backgroundSize: '48% auto',
  },
}));

const sectionTitleStyle = {
  ...newHFLTypography.eyebrowM,
  paddingBottom: '12px',
  marginBottom: '32px',
  borderBottom: '1px solid rgba(40, 15, 28, 0.35)',
};

const fieldLabelStyle = {
  ...newHFLTypography.ctaM,
  display: 'block',
  marginBottom: '8px',
};

const fieldStyle = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: 0,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(40, 15, 28, 0.35)',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--Text-Default-Primary, #280F1C)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--Text-Default-Primary, #280F1C)',
    borderWidth: '1px',
  },
  '& .MuiInputBase-input': {
    ...newHFLTypography.ctaM,
    padding: '14px 16px',
  },
  '& .MuiSelect-select': {
    ...newHFLTypography.ctaM,
    padding: '14px 16px',
  },
};

const mobilePrefixStyle = {
  marginRight: 0,
  paddingLeft: '16px',
  '& .MuiTypography-root': {
    ...newHFLTypography.ctaM,
    lineHeight: 'normal',
  },
};

const mobileFieldStyle = {
  ...fieldStyle,
  '& .MuiInputBase-input': {
    ...newHFLTypography.ctaM,
    padding: '14px 16px 14px 6px',
  },
};

const helperTextStyle = {
  ...newHFLTypography.ctaS,
  marginLeft: 0,
  marginTop: '8px',
};

const checkboxLabelStyle = {
  ...newHFLTypography.bodyM,
};

const inlineLinkStyle = {
  ...newHFLTypography.bodyM,
  textDecorationLine: 'underline',
  textDecorationStyle: 'solid',
  textDecorationSkipInk: 'none',
  textDecorationThickness: 'auto',
  textUnderlineOffset: 'auto',
  textUnderlinePosition: 'from-font',
  textDecorationColor: 'var(--Text-Default-Primary, #280F1C)',
  cursor: 'pointer',
};

const nextButtonStyle = {
  ...newHFLTypography.ctaM,
  color: 'var(--text-on-cta-primary, #FFF)',
  alignSelf: 'flex-start',
  backgroundColor: 'var(--Background-CTA-Primary, #185AEE)',
  border: '1px solid var(--Background-CTA-Primary, #185AEE)',
  borderRadius: 0,
  padding: '10px 20px',
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: 'var(--Background-CTA-Primary, #185AEE)',
    boxShadow: 'none',
    opacity: 0.9,
  },
};

const uploadButtonStyle = {
  ...newHFLTypography.ctaS,
  color: 'var(--Text-Default-Primary, #280F1C)',
  border: '1px solid var(--Border-Default-Primary, #280F1C)',
  background: 'var(--Background-CTA-Invert, #FFF)',
  borderRadius: 0,
  padding: '8px 18px',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': {
    border: '1px solid var(--Border-Default-Primary, #280F1C)',
    background: 'var(--Background-CTA-Invert, #FFF)',
    boxShadow: 'none',
    opacity: 0.9,
  },
};

const checkboxStyle = {
  width: '24px',
  height: '24px',
  flexShrink: 0,
  padding: 0,
  marginRight: '10px',
  color: 'var(--Border-Default-Primary, #280F1C) !important',
  borderRadius: 0,
  '&.Mui-checked': {
    color: 'var(--Border-Default-Primary, #280F1C) !important',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '24px',
    color: 'var(--Border-Default-Primary, #280F1C) !important',
  },
};


function RequiredLabel({ text, asteriskFontSize }: { text: string; asteriskFontSize?: string }) {
  return (
    <Box component="span">
      {text}
      <Box component="span" sx={{ color: '#C8102E', fontSize: asteriskFontSize }}>
        {' *'}
      </Box>
    </Box>
  );
}

function DashboardPage() {
  const navigate = useNavigate();
  const resumeInputRef = useRef<HTMLInputElement>(null);
  const originHost = window.location.origin;

  const [jobTitle, setJobTitle] = useState('');
  const [jobFunction, setJobFunction] = useState('');
  const [resumeName, setResumeName] = useState('');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [industrySector, setIndustrySector] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState(DEFAULT_COUNTRY_VALUE);
  const [mobileNumber, setMobileNumber] = useState('');
  const [email, setEmail] = useState('');
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [errors, setErrors] = useState<any>({});

  const sectorOptions = formData.industrySectorNew(industry) || [];

  const validateText = (value: string) =>
    validateFields.validate({ textFieldWithLength: value }, validationOptions)?.error?.details[0]?.message;

  const validateName = (value: string) =>
    validateFields.validate({ name: value }, validationOptions)?.error?.details[0]?.message;

  const validateDropDown = (value: string) =>
    validateFields.validate({ dropDown: value }, validationOptions)?.error?.details[0]?.message;

  const validateEmail = (value: string) =>
    validateFields.validate({ email: value }, validationOptions)?.error?.details[0]?.message;

  const validatePrivacyPolicy = (value: boolean) =>
    validateFields.validate({ privacyPolicy: value }, validationOptions)?.error?.details[0]?.message;


  const validateMobileNumber = (value: string, countryValue: string) => {
    if (!value) {
      return 'This field may not be blank.';
    }
    const selectedCountry = COUNTRIES.find((c) => c.country_value === countryValue);
    if (!selectedCountry) {
      return undefined;
    }
    const countryCode = selectedCountry.country_code;
    try {
      const parsed = phoneUtil.parse(value, countryCode.toUpperCase());
      let validationRegion = countryCode.toUpperCase();
      if (countryCode === 'us') {
        const areaCode = value.substring(0, 3);
        if (areaCode === '787' || areaCode === '939') {
          validationRegion = 'PR';
        }
      }
      if (!phoneUtil.isValidNumberForRegion(parsed, validationRegion)) {
        let countryRegion;
        switch (countryCode) {
          case 'us':
            countryRegion = 'U.S.';
            break;
          case 'gb':
            countryRegion = 'UK';
            break;
          default:
            countryRegion = countryCode?.toUpperCase();
            break;
        }
        return `Mobile number is not valid for ${countryRegion} region`;
      }
    } catch {
      return 'The string supplied did not seem to be a phone number';
    }
    return undefined;
  };

  const mobileCountryCode =
    COUNTRIES.find((c) => c.country_value === country)?.mobile_country_code || DEFAULT_MOBILE_COUNTRY_CODE;



  const refreshErrorWhileTyping = (
    key: string,
    value: string,
    validator: (fieldValue: string) => string | undefined,
  ) => {
    setErrors((prev: any) => {
      if (!prev[key]) return prev;
      const message = validator(value);
      if (message === prev[key]) return prev;
      return { ...prev, [key]: message };
    });
  };

  const handleIndustryChange = (value: string) => {
    setIndustry(value);
    setIndustrySector('');
    setErrors((prev: any) => ({ ...prev, industry: validateDropDown(value), industry_sector: undefined }));
  };

  const getSize = (sizeInKb: number) => {
    if (sizeInKb > 1024) return `${(sizeInKb / 1024).toFixed(2)} MB`;
    return `${sizeInKb} KB`;
  };

  const resumeDetails = resumeFile
    ? `${resumeFile.name.split('.').pop()?.toLowerCase()} ${getSize(Math.round(resumeFile.size / 1024))}`
    : '';

  const handleResumeUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    const rejectResume = (message: string) => {
      setResumeName('');
      setResumeFile(null);
      setErrors((prev: any) => ({ ...prev, resume: message }));
    };
    if (!ACCEPTED_RESUME_MIME_TYPES.includes(file.type)) {
      rejectResume('Please upload correct format: PDF (preferred), DOCX, DOC, RTF, TXT');
      return;
    }
    if (file.size > MAX_RESUME_BYTES) {
      rejectResume('File size is too big');
      return;
    }
    if (!RESUME_NAME_PATTERN.test(file.name)) {
      rejectResume('File name should not contain HTML Tags / Special Characters');
      return;
    }
    if (file.name.length > MAX_RESUME_NAME_LENGTH) {
      rejectResume('File name should not contain more than 100 characters');
      return;
    }
    setResumeName(file.name);
    setResumeFile(file);
    setErrors((prev: any) => ({ ...prev, resume: undefined }));
  };

  const handleResumeDelete = () => {
    setResumeName('');
    setResumeFile(null);
    setErrors((prev: any) => ({ ...prev, resume: undefined }));
  };

  const handleSubmit = async () => {
    const nextErrors = {
      job_title: validateText(jobTitle),
      job_function: validateDropDown(jobFunction),
      resume: validateText(resumeName),
      company_name: validateText(companyName),
      industry: validateDropDown(industry),
      industry_sector: validateDropDown(industrySector),
      first_name: validateName(firstName),
      last_name: validateName(lastName),
      country: validateDropDown(country),
      mobile_number: validateMobileNumber(mobileNumber, country),
      email: validateEmail(email),
      privacy_policy: validatePrivacyPolicy(privacyPolicy),
    };

    setErrors(nextErrors);

    if (Object.values(nextErrors).some((message) => Boolean(message))) {
      return;
    }

    eventBus.dispatch('toggleLoadingOn');

    try {
      const formDataPayload = new FormData();
      formDataPayload.append('email', email);
      formDataPayload.append('first_name', firstName);
      formDataPayload.append('last_name', lastName);
      formDataPayload.append('country', country);
      formDataPayload.append('mobile_number', `${mobileCountryCode.replace('+', '')}${mobileNumber.replace(/\D/g, '')}`);
      formDataPayload.append('mobile_country_code', mobileCountryCode);
      formDataPayload.append('job_title', jobTitle);
      formDataPayload.append('job_function', jobFunction);
      formDataPayload.append('company_name', companyName);
      formDataPayload.append('industry', industry);
      formDataPayload.append('industry_sector', industrySector);
      formDataPayload.append('privacy_policy', privacyPolicy.toString());
      formDataPayload.append('resume_file', resumeFile as File);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/edge/submit-profile/`,
        formDataPayload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      eventBus.dispatch('toggleLoadingOff');

      if (response.data?.status === 'success') {
        navigate('/candidate-profile/confirmation');
      } else {
        const errorMessage = response.data?.message || 'An error occurred while submitting your profile.';
        setErrors({ submit: errorMessage });
      }
    } catch (error: any) {
      eventBus.dispatch('toggleLoadingOff');
      const errorMessage = error.response?.data?.message || 'An error occurred while submitting your profile. Please try again.';
      setErrors({ submit: errorMessage });
    }
  };

  return (
    <NewHFLLayout>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: 'flex-start',
          gap: { xs: '48px', lg: '80px' },
          paddingLeft: { xs: '20px', md: '80px' },
          paddingRight: { xs: '20px', md: '80px' },
          paddingTop: { xs: '40px', md: '72px' },
          paddingBottom: { xs: '56px', md: '96px' },
        }}
      >
        <SCBackImage aria-hidden="true" />
        <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: { lg: '640px' } }}>
          <Typography component="h1" sx={{ ...newHFLTypography.headingXXL, marginBottom: '24px' }}>
            Submit your interest in leadership positions with our clients
          </Typography>
          <Typography component="p" sx={{ ...newHFLTypography.bodyL, marginBottom: '48px' }}>
            Please provide your contact information, a little bit about your professional background
            and your resume/CV
          </Typography>

          <Box component="form" noValidate onSubmit={(event: React.FormEvent) => event.preventDefault()}>
            <Typography component="h2" sx={sectionTitleStyle}>
              Your current or most recent position
            </Typography>
            <Stack spacing={4} sx={{ marginBottom: '56px' }}>
              <Box>
                <Typography component="label" htmlFor="newhfl-job-title" sx={fieldLabelStyle}>
                  <RequiredLabel text="Job title" />
                </Typography>
                <TextField
                  id="newhfl-job-title"
                  variant="outlined"
                  sx={fieldStyle}
                  value={jobTitle}
                  onChange={({ target }) => {
                    setJobTitle(target.value);
                    refreshErrorWhileTyping('job_title', target.value, validateText);
                  }}
                  onBlur={() => setErrors((prev: any) => ({ ...prev, job_title: validateText(jobTitle) }))}
                  error={Boolean(errors?.job_title)}
                  helperText={errors?.job_title || ''}
                  FormHelperTextProps={{ sx: helperTextStyle }}
                  inputProps={{ 'data-testid': 'newhfl-job-title' }}
                />
              </Box>
              <Box>
                <Typography component="label" htmlFor="newhfl-function" sx={fieldLabelStyle}>
                  <RequiredLabel text="Function" />
                </Typography>
                <TextField
                  id="newhfl-function"
                  select
                  variant="outlined"
                  sx={fieldStyle}
                  value={jobFunction}
                  onChange={({ target }) => {
                    setJobFunction(target.value);
                    setErrors((prev: any) => ({ ...prev, job_function: validateDropDown(target.value) }));
                  }}
                  error={Boolean(errors?.job_function)}
                  helperText={errors?.job_function || ''}
                  FormHelperTextProps={{ sx: helperTextStyle }}
                  inputProps={{ 'data-testid': 'newhfl-function' }}
                >
                  {formData.jobFunctionMenuNew.map((option: any) => (
                    <MenuItem key={option.keyValue} value={option.keyValue} sx={newHFLTypography.ctaM}>
                      {option.keyName}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', marginBottom: '16px' }}>
                  <UploadFileIcon
                    sx={{
                      width: '18px',
                      height: '22px',
                      fill: 'var(--Icon-Default-Primary, #280F1C)',
                    }}
                  />
                  <Typography component="p" sx={newHFLTypography.ctaS}>
                    {resumeFile
                      ? 'Your resume will be added to our database once you submit.'
                      : <RequiredLabel text="Add Resume" asteriskFontSize="18px" />}
                  </Typography>
                </Stack>
                <Typography component="p" sx={{ ...newHFLTypography.bodyM, marginBottom: resumeFile ? '4px' : '16px' }}>
                  {resumeName || 'PDF (Preferred), DOCX, DOC, RTF, TXT, up to 2MB'}
                </Typography>
                {resumeFile && (
                  <Typography component="p" sx={{ ...newHFLTypography.ctaS, marginBottom: '16px' }}>
                    {resumeDetails}
                  </Typography>
                )}
                <Box>
                  <Box
                    component="input"
                    ref={resumeInputRef}
                    type="file"
                    hidden
                    accept={ACCEPTED_RESUME_TYPES}
                    onChange={handleResumeUpload}
                    data-testid="newhfl-resume-input"
                  />
                  {resumeFile ? (
                    <Button
                      variant="outlined"
                      sx={uploadButtonStyle}
                      startIcon={<CloseIcon sx={{ fontSize: '18px' }} />}
                      onClick={handleResumeDelete}
                      data-testid="newhfl-resume-delete"
                    >
                      Delete
                    </Button>
                  ) : (
                    <Button variant="outlined" sx={uploadButtonStyle} onClick={() => resumeInputRef.current?.click()}>
                      Upload
                    </Button>
                  )}
                </Box>
                {errors?.resume && <FormHelperText error sx={helperTextStyle}>{errors.resume}</FormHelperText>}
              </Box>
            </Stack>

            <Typography component="h2" sx={sectionTitleStyle}>
              Your current or most recent company
            </Typography>
            <Stack spacing={4} sx={{ marginBottom: '56px' }}>
              <Box>
                <Typography component="label" htmlFor="newhfl-company-name" sx={fieldLabelStyle}>
                  <RequiredLabel text="Company name" />
                </Typography>
                <TextField
                  id="newhfl-company-name"
                  variant="outlined"
                  sx={fieldStyle}
                  value={companyName}
                  onChange={({ target }) => {
                    setCompanyName(target.value);
                    refreshErrorWhileTyping('company_name', target.value, validateText);
                  }}
                  onBlur={() => setErrors((prev: any) => ({ ...prev, company_name: validateText(companyName) }))}
                  error={Boolean(errors?.company_name)}
                  helperText={errors?.company_name || ''}
                  FormHelperTextProps={{ sx: helperTextStyle }}
                  inputProps={{ 'data-testid': 'newhfl-company-name' }}
                />
              </Box>
              <Box>
                <Typography component="label" htmlFor="newhfl-industry" sx={fieldLabelStyle}>
                  <RequiredLabel text="Industry" />
                </Typography>
                <TextField
                  id="newhfl-industry"
                  select
                  variant="outlined"
                  sx={fieldStyle}
                  value={industry}
                  onChange={({ target }) => handleIndustryChange(target.value)}
                  error={Boolean(errors?.industry)}
                  helperText={errors?.industry || ''}
                  FormHelperTextProps={{ sx: helperTextStyle }}
                  inputProps={{ 'data-testid': 'newhfl-industry' }}
                >
                  {formData.industryMenuNew.map((option: any) => (
                    <MenuItem key={option.keyValue} value={option.keyValue} sx={newHFLTypography.ctaM}>
                      {option.keyName}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box>
                <Typography component="label" htmlFor="newhfl-industry-sector" sx={fieldLabelStyle}>
                  <RequiredLabel text="Industry sector" />
                </Typography>
                <TextField
                  id="newhfl-industry-sector"
                  select
                  variant="outlined"
                  sx={fieldStyle}
                  value={industrySector}
                  disabled={!industry}
                  onChange={({ target }) => {
                    setIndustrySector(target.value);
                    setErrors((prev: any) => ({ ...prev, industry_sector: validateDropDown(target.value) }));
                  }}
                  error={Boolean(errors?.industry_sector)}
                  helperText={errors?.industry_sector || ''}
                  FormHelperTextProps={{ sx: helperTextStyle }}
                  inputProps={{ 'data-testid': 'newhfl-industry-sector' }}
                >
                  {sectorOptions.map((option: any) => (
                    <MenuItem key={option.keyValue} value={option.keyValue} sx={newHFLTypography.ctaM}>
                      {option.keyName}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>

            <Typography component="h2" sx={sectionTitleStyle}>
              Contact details
            </Typography>
            <Stack spacing={4} sx={{ marginBottom: '40px' }}>
              <Box>
                <Typography component="label" htmlFor="newhfl-first-name" sx={fieldLabelStyle}>
                  <RequiredLabel text="First Name" />
                </Typography>
                <TextField
                  id="newhfl-first-name"
                  variant="outlined"
                  sx={fieldStyle}
                  value={firstName}
                  onChange={({ target }) => {
                    setFirstName(target.value);
                    refreshErrorWhileTyping('first_name', target.value, validateName);
                  }}
                  onBlur={() => setErrors((prev: any) => ({ ...prev, first_name: validateName(firstName) }))}
                  error={Boolean(errors?.first_name)}
                  helperText={errors?.first_name || ''}
                  FormHelperTextProps={{ sx: helperTextStyle }}
                  inputProps={{ 'data-testid': 'newhfl-first-name' }}
                />
              </Box>
              <Box>
                <Typography component="label" htmlFor="newhfl-last-name" sx={fieldLabelStyle}>
                  <RequiredLabel text="Last Name" />
                </Typography>
                <TextField
                  id="newhfl-last-name"
                  variant="outlined"
                  sx={fieldStyle}
                  value={lastName}
                  onChange={({ target }) => {
                    setLastName(target.value);
                    refreshErrorWhileTyping('last_name', target.value, validateName);
                  }}
                  onBlur={() => setErrors((prev: any) => ({ ...prev, last_name: validateName(lastName) }))}
                  error={Boolean(errors?.last_name)}
                  helperText={errors?.last_name || ''}
                  FormHelperTextProps={{ sx: helperTextStyle }}
                  inputProps={{ 'data-testid': 'newhfl-last-name' }}
                />
              </Box>
              <Box>
                <Typography component="label" htmlFor="newhfl-country" sx={fieldLabelStyle}>
                  <RequiredLabel text="Country of Residence" />
                </Typography>
                <TextField
                  id="newhfl-country"
                  select
                  variant="outlined"
                  sx={fieldStyle}
                  value={country}
                  onChange={({ target }) => {
                    setCountry(target.value);
                    setErrors((prev: any) => ({
                      ...prev,
                      country: validateDropDown(target.value),
                      mobile_number: mobileNumber ? validateMobileNumber(mobileNumber, target.value) : undefined,
                    }));
                  }}
                  error={Boolean(errors?.country)}
                  helperText={errors?.country || ''}
                  FormHelperTextProps={{ sx: helperTextStyle }}
                  inputProps={{ 'data-testid': 'newhfl-country' }}
                >
                  {formData.countryList.map((option: any) => (
                    <MenuItem key={option.keyValue} value={option.keyValue} sx={newHFLTypography.ctaM}>
                      {option.keyName}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box>
                <Typography component="label" htmlFor="newhfl-mobile-number" sx={fieldLabelStyle}>
                  <RequiredLabel text="Mobile Number" />
                </Typography>
                <TextField
                  id="newhfl-mobile-number"
                  variant="outlined"
                  sx={mobileFieldStyle}
                  value={mobileNumber}
                  onChange={({ target }) => {
                    const numericValue = target.value.replace(/[^0-9]/g, '');
                    setMobileNumber(numericValue);
                    setErrors((prev: any) => ({
                      ...prev,
                      mobile_number: numericValue ? validateMobileNumber(numericValue, country) : undefined,
                    }));
                  }}
                  onBlur={() =>
                    setErrors((prev: any) => ({ ...prev, mobile_number: validateMobileNumber(mobileNumber, country) }))
                  }
                  error={Boolean(errors?.mobile_number)}
                  helperText={errors?.mobile_number || ''}
                  FormHelperTextProps={{ sx: helperTextStyle }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={mobilePrefixStyle}>
                        {mobileCountryCode}
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ 'data-testid': 'newhfl-mobile-number', inputMode: 'tel' }}
                />
              </Box>
              <Box>
                <Typography component="label" htmlFor="newhfl-email" sx={fieldLabelStyle}>
                  <RequiredLabel text="Email" />
                </Typography>
                <TextField
                  id="newhfl-email"
                  variant="outlined"
                  sx={fieldStyle}
                  value={email}
                  onChange={({ target }) => {
                    setEmail(target.value);
                    refreshErrorWhileTyping('email', target.value, validateEmail);
                  }}
                  onBlur={() => setErrors((prev: any) => ({ ...prev, email: validateEmail(email) }))}
                  error={Boolean(errors?.email)}
                  helperText={errors?.email || ''}
                  FormHelperTextProps={{ sx: helperTextStyle }}
                  inputProps={{ 'data-testid': 'newhfl-email', inputMode: 'email' }}
                />
              </Box>
            </Stack>

            <Box sx={{ marginBottom: '40px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={privacyPolicy}
                    onChange={({ target }) => {
                      setPrivacyPolicy(target.checked);
                      setErrors((prev: any) => ({ ...prev, privacy_policy: validatePrivacyPolicy(target.checked) }));
                    }}
                    sx={checkboxStyle}
                    inputProps={{ 'aria-label': 'I agree to the Privacy Policy' } as any}
                  />
                }
                label={
                  <Typography component="span" sx={checkboxLabelStyle}>
                    I agree to the{' '}
                    <Link
                      sx={inlineLinkStyle}
                      onClick={() => window.open(`${originHost}/res/privacy-policy.pdf`, '_blank', 'noopener,noreferrer')}
                      tabIndex={0}
                    >
                      Privacy Policy
                    </Link>
                    .
                  </Typography>
                }
                sx={{ alignItems: 'flex-start', marginLeft: 0 }}
              />
              {errors?.privacy_policy && (
                <FormHelperText error sx={helperTextStyle}>
                  {errors.privacy_policy}
                </FormHelperText>
              )}

            </Box>

            {errors?.submit && (
              <Box
                sx={{
                  marginBottom: '24px',
                  padding: '12px 16px',
                  backgroundColor: '#FFEBEE',
                  border: '1px solid #EF5350',
                  borderRadius: '4px',
                }}
              >
                <FormHelperText error sx={{ ...helperTextStyle, margin: 0 }}>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}

            <Button
              type="button"
              variant="contained"
              endIcon={<ArrowForwardIcon sx={{ fontSize: '16px' }} />}
              sx={nextButtonStyle}
              onClick={handleSubmit}
              data-testid="newhfl-submit-next"
            >
              Next
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
            flex: 1,
          }}
        />
      </Box>
    </NewHFLLayout>
  );
}

export default DashboardPage;
